import { useState, useEffect } from "react"
import { formatEther } from "viem"
import { useChainId } from "wagmi"
import { publicClients } from "../lib/wagmi"
import { childPool, newPool } from "../utils/contract"
import { useTokenPrice } from "./useTokenPrice"

export const useTVL = (contractAddress, poolType, tokenAddress) => {
    const [tvl, setTvl] = useState(null)
    const [rawTvl, setRawTvl] = useState(null)
    const [tvlUSD, setTvlUSD] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const chainId = useChainId()

    // Use the useTokenPrice hook to get the token price only if tokenAddress is provided
    const { price: tokenPriceUSD, loading: priceLoading, error: priceError } = useTokenPrice(
        tokenAddress || null,
        chainId
    )

    useEffect(() => {
        if (!contractAddress || !poolType) return

        const fetchTVL = async () => {
            try {
                setLoading(true)
                setError(null)

                let abi, functionName

                if (poolType === "childPool") {
                    abi = newPool.abi
                    functionName = "totalStaked"
                } else if (poolType === "rootPool") {
                    abi = childPool.abi
                    functionName = "tvl"
                } else {
                    throw new Error(`Unknown poolType: ${poolType}`)
                }

                // 1️⃣ Get raw TVL value
                const result = await publicClients[chainId].readContract({
                    address: contractAddress,
                    abi,
                    functionName,
                    args: [],
                })

                const tvlValue = parseFloat(formatEther(result))
                setTvl(tvlValue)
                setRawTvl(result)
            } catch (err) {
                console.error("Error fetching TVL:", err)
                setError(err)
                setTvl(null)
                setRawTvl(null)
                setTvlUSD(null)
            } finally {
                setLoading(false)
            }
        }

        fetchTVL()
    }, [contractAddress, poolType, chainId, tokenAddress])

    // 3️⃣ Calculate TVL in USD when we have both tvl and token price (only if tokenAddress is provided)
    useEffect(() => {
        if (tokenAddress && tvl !== null && tokenPriceUSD !== null) {
            const tvlInUSD = tvl * tokenPriceUSD
            setTvlUSD(tvlInUSD)
        } else if (!tokenAddress) {
            // If no tokenAddress provided, set tvlUSD to null
            setTvlUSD(null)
        }
    }, [tvl, tokenPriceUSD, tokenAddress])

    // Combine loading states (only include price loading if tokenAddress is provided)
    const isLoading = loading || (tokenAddress ? priceLoading : false)

    // Combine errors (only include price error if tokenAddress is provided)
    const combinedError = error || (tokenAddress ? priceError : null)

    return { tvl, rawTvl, tvlUSD, loading: isLoading, error: combinedError }
}